import { t } from "i18next";
import * as React from "react";
import {
  AddButton,
  CancelButton,
} from "../addClientModal/addClientModal.styles";
import { UserContext } from "../../../contexts/user.context";
import { Patch, Post, handleError } from "../../../utils/fetch.util";
import LoadingData from "../../loadingData/loadingData.component";
import { toast } from "react-toastify";
import CreatableSelect from "react-select/creatable";

interface VisitorBlackList {
  national_id: string;
  PassportNumber?: string;
  name: string;
  address: string;
  job_title: string;
  block_reasons: string[];
  block_date: string;
  block_endDate: string;
  block_duration: number;
  isBlockedForever: boolean;
}

interface AddVisitorBlackListModelProps {
  isOpen: boolean;
  onClose: () => void;
  getAllVisitorsBlackList: () => void;
  editVisitorBlackList?: any;
}

const AddVisitorBlackListModel: React.FC<AddVisitorBlackListModelProps> = ({
  isOpen,
  onClose,
  getAllVisitorsBlackList,
  editVisitorBlackList,
}) => {
  const defaultVisitorBlackList = {
    national_id: "",
    name: "",
    address: "",
    job_title: "",
    block_reasons: [],
    block_date: "",
    block_endDate: "",
    block_duration: 1,
    isBlockedForever: false,
  };
  const [visitorBlackList, setVisitorBlackList] =
    React.useState<VisitorBlackList>(defaultVisitorBlackList);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const { getValidToken } = React.useContext(UserContext);

  const handleChange = (e: any) => {
    setVisitorBlackList({
      ...visitorBlackList,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true);

    const res = await Post(
      "visitors-black-list/add-visitor-blackList",
      {
        ...visitorBlackList,
        block_duration: visitorBlackList.isBlockedForever
          ? 0
          : Number(visitorBlackList.block_duration),
        birthday: getBirthdateFromNId(visitorBlackList.national_id),
      },
      getValidToken()
    );

    if (res.error || !res.data) {
      handleError(res);
      setIsLoading(false);
      return;
    }
    getAllVisitorsBlackList();
    setVisitorBlackList(defaultVisitorBlackList);
    onClose();
    setIsLoading(false);
  };

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const res = await Patch(
      `visitors-black-list/update-visitor-blackList`,
      {
        ...visitorBlackList,
        blackList_id: editVisitorBlackList.id,
        block_duration: visitorBlackList.isBlockedForever
          ? 0
          : Number(visitorBlackList.block_duration),
        birthday: getBirthdateFromNId(visitorBlackList.national_id),
      },
      getValidToken()
    );

    if (res.error || !res.data) {
      handleError(res);
      setIsLoading(false);
      return;
    }

    toast.success("updated");
    onClose();
    setIsLoading(false);
    setVisitorBlackList(defaultVisitorBlackList);
    getAllVisitorsBlackList();
  };

  const getBirthdateFromNId = (id: string) => {
    // Extract century, year, and month-day part
    const centuryCode = id[0];
    const yearPart = id.substring(1, 3);
    const monthDayPart = id.substring(3, 7);

    // Define century ranges
    const centuryRanges: any = {
      "1": [1800, 1900],
      "2": [1900, 2000],
      "3": [2000, 2100],
      "4": [2100, 2200],
    };

    // Get the start year of the century
    const startYearOfCentury = centuryRanges[centuryCode][0];

    // Convert year part to actual year
    const year = parseInt(yearPart) + startYearOfCentury;

    // Extract month and day
    const month = monthDayPart.substring(0, 2);
    const day = monthDayPart.substring(2, 4);

    // Return formatted date
    return `${year}-${month}-${day}`;
  };

  React.useEffect(() => {
    setVisitorBlackList({
      national_id: editVisitorBlackList?.nationl_id,
      name: editVisitorBlackList?.name,
      address: editVisitorBlackList?.address,
      job_title: editVisitorBlackList?.job,
      block_reasons: editVisitorBlackList?.BlackListReason.map(
        (item: any) => item.reason
      ),
      block_date: editVisitorBlackList?.block_date,
      block_endDate: editVisitorBlackList?.block_endDate.split("T")[0],
      block_duration: editVisitorBlackList?.block_duration,
      isBlockedForever: editVisitorBlackList?.isBlockedForever,
    });
  }, [editVisitorBlackList]);

  if (isLoading) return <LoadingData />;

  return (
    <>
      <div
        className={`bg-black cursor-pointer ${
          isOpen ? "opacity-60" : "opacity-0 pointer-events-none"
        } fixed inset-0 z-[99999] flex items-center justify-center transition-all duration-500`}
        onClick={onClose}
      ></div>
      <form
        className={`fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white w-[600px] p-6 !z-[99999] ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onSubmit={editVisitorBlackList ? handleUpdate : handleSubmit}
      >
        <h2 className="text-3xl mb-6 !text-black">
          {editVisitorBlackList
            ? t("edit_visitor")
            : t("add_black_list_visitor")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-2 items-center justify-center">
          <div className="flex flex-col gap-2 mt-5">
            <label aria-required>{t("visitor_name")}</label>
            <input
              onChange={handleChange}
              value={visitorBlackList.name}
              name="name"
              className="border rounded"
              type="text"
              required
            />
          </div>
          <div className="flex flex-col gap-2 mt-5">
            <label aria-required>{t("address")}</label>
            <input
              onChange={handleChange}
              value={visitorBlackList.address}
              name="address"
              className="border rounded"
              type="text"
              required
            />
          </div>
          <div className="flex flex-col gap-2 mt-5">
            <label>{t("national_id")}</label>
            <input
              onChange={(e) => {
                setVisitorBlackList({
                  ...visitorBlackList,
                  national_id: e.target.value,
                });
              }}
              // title={"Max number of digits is 18"}
              // pattern="\d{18}"
              value={visitorBlackList.national_id}
              name="national_id"
              className="border rounded"
              type="text"
            />
          </div>

          <div className="flex flex-col gap-2 mt-5">
            <label>{t("passport_id")}</label>
            <input
              onChange={(e) => {
                setVisitorBlackList({
                  ...visitorBlackList,
                  PassportNumber: e.target.value,
                });
              }}
              // title={"Max number of digits is 18"}
              // pattern="\d{18}"
              value={visitorBlackList.PassportNumber}
              name="PassportNumber"
              className="border rounded"
              type="text"
            />
          </div>

          <div className="flex flex-col gap-2 mt-5">
            <label aria-required>{t("job_title")}</label>
            <input
              onChange={handleChange}
              value={visitorBlackList.job_title}
              name="job_title"
              className="border rounded"
              type="text"
              required
            />
          </div>
          <div className="flex flex-col gap-2 mt-5">
            <label aria-required>{t("block_start_date")}</label>
            <input
              onChange={handleChange}
              value={visitorBlackList.block_date?.split("T")[0]}
              name="block_date"
              className="border rounded"
              type="date"
              min={new Date().toISOString().split("T")[0]}
              required
            />
          </div>
          {!visitorBlackList.isBlockedForever && (
            <div className="flex flex-col gap-2 mt-5">
              <label aria-required>{t("block_end_date")}</label>
              <input
                required
                value={visitorBlackList?.block_endDate || ""}
                onChange={(e) => {
                  const end_date = new Date(e.target.value);
                  const start_date = new Date(visitorBlackList.block_date);
                  if (end_date < start_date) {
                    toast.error(t("end_date_must_be_after_start_date"));
                    return;
                  }
                  const block_duration = Math.ceil(
                    (end_date.getTime() - start_date.getTime()) /
                      (1000 * 3600 * 24)
                  );
                  setVisitorBlackList((prev) => ({
                    ...prev,
                    block_duration,
                    block_endDate: e.target.value,
                  }));
                }}
                name="block_end_date"
                className="border rounded"
                type="date"
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
          )}
          <div className="flex flex-col gap-2 mt-5">
            <label aria-required>{t("block_reasons")}</label>
            <CreatableSelect
              required
              isClearable
              isMulti
              closeMenuOnSelect={false}
              value={visitorBlackList?.block_reasons?.map((item) => {
                return {
                  label: item,
                  value: item,
                };
              })}
              onChange={(selectedOption) => {
                // console.log(selectedOption);
                setVisitorBlackList((prev) => ({
                  ...prev,
                  block_reasons: selectedOption
                    ? selectedOption.map((item: any) => item.label)
                    : [],
                }));
              }}
              placeholder={t("block_reasons")}
            />
          </div>
          <div className="flex items-center justify-between gap-2 mt-5">
            <label>{t("is_blocked_forever")}</label>
            <input
              onChange={(e) => {
                setVisitorBlackList((prev) => ({
                  ...prev,
                  isBlockedForever: e.target.checked,
                }));
              }}
              checked={visitorBlackList.isBlockedForever}
              name="isBlockedForever"
              className="w-5 h-5"
              type="checkbox"
            />
          </div>
        </div>
        <div
          className="w-full flex items-center gap-12 mt-8"
          style={{ gap: "2rem" }}
        >
          <AddButton isLoading={isLoading} disabled={isLoading} type="submit">
            {editVisitorBlackList
              ? t("edit_visitor")
              : t("add_black_list_visitor")}
          </AddButton>
          <CancelButton
            type="button"
            style={{ border: "2px solid black" }}
            onClick={() => {
              onClose();
              setVisitorBlackList(defaultVisitorBlackList);
            }}
          >
            {t("cancelBtn")}
          </CancelButton>
        </div>
      </form>
    </>
  );
};

export default AddVisitorBlackListModel;
